import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const NEW = '__nou__';
const num = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;
const r2 = (v) => Math.round(v * 100) / 100;

export default function FacturaManualForm({ initial, suppliers, onSaved, onCancel }) {
  const [f, setF] = useState({
    cod: initial?.cod || NEW, furnizor_nou: '', categorie_noua: '', tip_doc: initial?.tip_doc || 'Factură', nr_intrare: initial?.nr_intrare || '', data: initial?.data || new Date().toISOString().slice(0, 10),
    scadent: initial?.scadent || '', baza_tva: initial?.baza_tva ?? '', tva: initial?.tva ?? '', total: initial?.total ?? '', neachitat: initial?.neachitat ?? '', inf_suplm: initial?.inf_suplm || '',
  });
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [tipuri, setTipuri] = useState(['Factură']);
  const [tipNou, setTipNou] = useState('');
  useEffect(() => {
    axios.get(`${API_URL}/furnizori/categorii`, auth()).then((r) => setCats(r.data)).catch(() => {});
    axios.get(`${API_URL}/furnizori/tipuri-doc`, auth()).then((r) => setTipuri(r.data)).catch(() => {});
  }, []);
  const addTip = async () => {
    const n = tipNou.trim(); if (!n) return;
    try { const r = await axios.post(`${API_URL}/furnizori/tipuri-doc`, { nume: n }, auth()); setTipuri(r.data); setF((s) => ({ ...s, tip_doc: r.data.find((t) => t.toLowerCase() === n.toLowerCase()) || n })); setTipNou(''); toast.success('Tip adăugat'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };
  const isFactura = (f.tip_doc || 'Factură') === 'Factură';
  const catSuggestions = Array.from(new Set([...cats, 'Instituție', 'Taxe și impozite', 'Chirie', 'Utilități', 'Piese auto', 'Combustibil', 'Leasing', 'Servicii']));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const autoTva = () => { const b = num(f.baza_tva); if (b && f.tva === '') setF((s) => ({ ...s, tva: String(r2(b * 0.21)), total: String(r2(b * 1.21)), neachitat: s.neachitat === '' ? String(r2(b * 1.21)) : s.neachitat })); };
  const autoTotal = () => { const b = num(f.baza_tva), t = num(f.tva); if (b || t) setF((s) => ({ ...s, total: String(r2(b + t)), neachitat: s.neachitat === '' ? String(r2(b + t)) : s.neachitat })); };
  const submit = async (e) => {
    e.preventDefault();
    const body = { cod: f.cod === NEW ? null : f.cod, furnizor_nou: f.cod === NEW ? f.furnizor_nou : null, categorie_noua: f.cod === NEW ? f.categorie_noua : null, tip_doc: f.tip_doc, nr_intrare: f.nr_intrare, data: f.data, scadent: f.scadent || null,
      baza_tva: num(f.baza_tva), tva: num(f.tva), total: num(f.total), neachitat: num(f.neachitat), inf_suplm: f.inf_suplm };
    if (f.cod === NEW && !f.furnizor_nou.trim()) return toast.error('Scrie numele furnizorului');
    if (!body.nr_intrare.trim()) return toast.error('Numărul documentului este obligatoriu');
    if (isFactura && !body.total) return toast.error('Totalul facturii este obligatoriu');
    setBusy(true);
    try {
      const r = initial?.id ? await axios.put(`${API_URL}/furnizori/facturi/${initial.id}`, body, auth()) : await axios.post(`${API_URL}/furnizori/facturi`, body, auth());
      toast.success(initial?.id ? 'Document actualizat' : 'Document adăugat'); onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="ff-manual-form">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Tip document *</Label>
          <Select value={f.tip_doc} onValueChange={(v) => setF((s) => ({ ...s, tip_doc: v }))}>
            <SelectTrigger className="h-8 text-sm" data-testid="ff-tip-doc"><SelectValue /></SelectTrigger>
            <SelectContent>{tipuri.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Tip nou <span className="text-slate-400">(dacă nu e în listă)</span></Label>
          <div className="flex gap-1">
            <Input className="h-8" value={tipNou} onChange={(e) => setTipNou(e.target.value)} placeholder="ex. Aviz, Proces-verbal" data-testid="ff-tip-nou" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTip(); } }} />
            <Button type="button" size="sm" variant="outline" className="h-8" onClick={addTip} disabled={!tipNou.trim()} data-testid="ff-tip-nou-add">+ Adaugă</Button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Furnizor *</Label>
          <Select value={f.cod} onValueChange={(v) => setF((s) => ({ ...s, cod: v }))}>
            <SelectTrigger className="h-8 text-sm" data-testid="ff-supplier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NEW}>+ Furnizor nou (instituție care nu e în SPV)</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.cod} value={s.cod}>{s.denumire}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {f.cod === NEW && <div><Label className="text-xs">Nume furnizor nou *</Label><Input className="h-8" value={f.furnizor_nou} onChange={set('furnizor_nou')} placeholder="ex. Primăria Slobozia" data-testid="ff-supplier-new" /></div>}
        {f.cod === NEW && (
          <div className="col-span-2">
            <Label className="text-xs">Categorie furnizor <span className="text-slate-400">(opțional, se poate schimba din fișa furnizorului)</span></Label>
            <Input className="h-8" list="ff-categorii" value={f.categorie_noua} onChange={set('categorie_noua')} placeholder="ex. Instituție, Taxe și impozite, Chirie" data-testid="ff-supplier-category" />
            <datalist id="ff-categorii">{catSuggestions.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">Nr. document *</Label><Input className="h-8" value={f.nr_intrare} onChange={set('nr_intrare')} data-testid="ff-nr" /></div>
        <div><Label className="text-xs">Data *</Label><Input className="h-8" type="date" value={f.data} onChange={set('data')} data-testid="ff-data" /></div>
        <div><Label className="text-xs">Scadență</Label><Input className="h-8" type="date" value={f.scadent} onChange={set('scadent')} data-testid="ff-scadent" /></div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div><Label className="text-xs">Bază (lei)</Label><Input className="h-8" inputMode="decimal" value={f.baza_tva} onChange={set('baza_tva')} onBlur={autoTva} data-testid="ff-baza" /></div>
        <div><Label className="text-xs">TVA (lei)</Label><Input className="h-8" inputMode="decimal" value={f.tva} onChange={set('tva')} onBlur={autoTotal} data-testid="ff-tva" /></div>
        <div><Label className="text-xs">Total{isFactura ? ' *' : ''}</Label><Input className="h-8 font-semibold" inputMode="decimal" value={f.total} onChange={set('total')} data-testid="ff-total" /></div>
        <div><Label className="text-xs">Neachitat (lei)</Label><Input className="h-8" inputMode="decimal" value={f.neachitat} onChange={set('neachitat')} data-testid="ff-neachitat" /></div>
      </div>
      <div><Label className="text-xs">Observații</Label><Input className="h-8" value={f.inf_suplm} onChange={set('inf_suplm')} data-testid="ff-obs" /></div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="ff-cancel">Anulează</Button>
        <Button type="submit" size="sm" disabled={busy} data-testid="ff-submit">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {initial?.id ? 'Salvează' : 'Adaugă documentul'}</Button>
      </div>
    </form>
  );
}
